import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';

export interface Entitlements {
    plan_code: string;
    plan_label: string;
    is_vip: boolean;
    expires_at: string | null;
    days_remaining: number | null;
    extra_day_cost_xu?: number;
    daily_rate?: number;
    features: {
        max_campaigns: number;
        max_active_campaigns: number;
        max_plays_month: number;
        max_plays_day?: number;
        game_types: string[];
        can_export: boolean;
        analytics_days: number;
        custom_form: boolean;
        webhook: boolean;
        remove_watermark: boolean;
        custom_background: boolean;
        ip_block: boolean;
        max_gifts: number;
        max_users: number;
    };
}

export function useEntitlements(campaignId?: string) {
    const { user } = useAuthStore();
    const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const impersonatedId = localStorage.getItem('impersonated_tenant_id');
    const tenantId = impersonatedId || user?.tenant_id;

    const fetchEntitlements = useCallback(async () => {
        if (!tenantId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const res = await api.getEntitlements(tenantId, campaignId);
            setEntitlements(res);
            setError(null);
        } catch (err: any) {
            console.error('Lỗi lấy thông tin gói cước:', err);
            setError(err.message || 'Không thể lấy thông tin gói cước');
        } finally {
            setLoading(false);
        }
    }, [tenantId, campaignId]);

    useEffect(() => {
        fetchEntitlements();
    }, [fetchEntitlements]);

    // Kiểm tra xem tính năng boolean có được kích hoạt không
    const hasFeature = useCallback((key: keyof Entitlements['features']): boolean => {
        if (user?.roles?.includes('super_admin')) return true;
        if (!entitlements) return false;
        const val = entitlements.features[key];
        return typeof val === 'boolean' ? val : false;
    }, [entitlements, user]);

    // Lấy giới hạn số lượng của tính năng
    const getLimit = useCallback((key: keyof Entitlements['features']): number => {
        if (user?.roles?.includes('super_admin')) return 999999;
        if (!entitlements) return 0;
        const val = entitlements.features[key];
        return typeof val === 'number' ? val : 0;
    }, [entitlements, user]);

    // Kiểm tra xem loại game có được cho phép không
    const hasGameType = useCallback((type: string): boolean => {
        if (user?.roles?.includes('super_admin')) return true;
        if (!entitlements) return false;
        return entitlements.features.game_types?.includes(type) ?? false;
    }, [entitlements, user]);

    return {
        entitlements,
        loading,
        error,
        hasFeature,
        getLimit,
        hasGameType,
        refresh: fetchEntitlements,
    };
}
